/** Game constants: the economy, the campaign store and roster, upgrades, claims. Part of src/constants.ts (re-exported there; import from '../constants'). */
import type { WeaponKey, GearUpgradeDef } from '../types';

// =============================================================================
// ECONOMY
// =============================================================================

export const DEFAULT_STARTING_CBP = 12;
export const ECOTOURISM_INCOME = 1;      // CBP per turn
export const ECOTOURISM_DESTROY_FINE = 2;
export const MAX_ECOTOURISM_PER_PLAYER = 1;
export const STRUCTURE_HEALTH = 20;
/**
 * An emplacement is a hardened gun position, not a shed: at the generic 20 any near miss (structures take damage out
 * to 2.2× the blast radius) destroyed it, so a seat lasted one shell. At 50 it survives one direct hit from every shell
 * but the largest, and two basic missiles still kill it.
 */
export const EMPLACEMENT_HEALTH = 50;
// (R12 step 2: a structure's starting health is its body's — constants/body.ts BODIES, core/body.ts structureHealth.)
/** S6 seats (core/seats.ts): what an emplacement's seat fires (the mounted classes — the gun's ammunition, not the unit's), and what mounting costs. */
export const EMPLACEMENT_WEAPONS: WeaponKey[] = ['standard', 'heavy'];
export const MOUNT_MOVEMENT_COST = 1.0;
export const DISMOUNT_MOVEMENT_COST = 0.5;
export const BUILD_MOVEMENT_COST = 4.0;    // movement points to place a structure
export const BUILD_PLACEMENT_RADIUS = 1.5; // max arc distance from player for placement
export const DELIVERY_COST_MULTIPLIER = 3;

// =============================================================================
// CAMPAIGN STORE — one-time weapon unlock prices (~5-8x per-shot cost)
// =============================================================================

export const CAMPAIGN_WEAPON_PRICES: Partial<Record<WeaponKey, number>> = {
  mortar: 20,
  heavy: 25,
  cluster: 40,
  nuke: 80,
  punch: 15,
  antigrav: 50,
  gravBomb: 30,
  tether: 25,
  terraformer: 25,
  emp: 25,
  drill: 35,
  fire: 25,
};

// =============================================================================
// CLAIMS
// =============================================================================

export const CLAIM_MOVEMENT_COST = 1.0;
export const CLAIM_RANGE = 2.0;
export const CLAIM_TURNS_REQUIRED = 2;
export const CLAIM_BREAK_RANGE = 3.0;

// =============================================================================
// CAMPAIGN ROSTER
// =============================================================================

export const MAX_CAMPAIGN_UNITS = 6;
export const MAX_MISSION_UNITS = 3;

export const RECRUIT_NAMES = [
  'Kessler', 'Vostok', 'Laika', 'Aldrin', 'Sagan', 'Korolev',
  'Tycho', 'Kepler', 'Glenn', 'Jemison', 'Lovell', 'Shepard',
  'Gagarin', 'Ride', 'Tereshkova', 'Hadfield', 'Chaffee', 'Yeager',
  'Cernan', 'Armstrong', 'Collins', 'Bean', 'Irwin', 'Schmitt',
];

// =============================================================================
// UNIT UPGRADES
// =============================================================================

export const UPGRADE_AP_COST = 150;
export const UPGRADE_SKILL_COST = 250;
export const UPGRADE_FITNESS_COST = 300;
export const UPGRADE_JUMP_COST = 300;
export const MAX_UPGRADE_LEVEL = 3;       // max level for fitness/jump upgrades
export const MAX_AP_LEVEL = 4;            // max AP per unit
export const FITNESS_MOVEMENT_BONUS = 1.0; // +1 movement point per fitness level
export const JUMP_HEIGHT_BONUS = 5.0;     // +5 jump velocity per jump level
export const SKILL_PROGRESSION = ['easy', 'medium', 'hard', 'expert'];

// =============================================================================
// GEAR UPGRADES — team-wide T1/T2/T3 progression
// =============================================================================

export const MAX_GEAR_TIER = 3;

export const GEAR_UPGRADES: Record<string, GearUpgradeDef> = {
  // ---- Shared upgrades (always visible) ----
  allDamage: {
    key: 'allDamage', category: 'shared', slot: 'launcher', name: 'Yield', description: 'Increase all weapon damage',
    icon: '⚔', tiers: [
      { cost: 80,  label: '+10% all damage', value: 1.10 },
      { cost: 160, label: '+20% all damage', value: 1.20 },
      { cost: 280, label: '+35% all damage', value: 1.35 },
    ],
  },
  allFuel: {
    key: 'allFuel', category: 'shared', slot: 'launcher', name: 'Range', description: 'Increase all weapon fuel duration',
    icon: '⛽', tiers: [
      { cost: 60,  label: '+15% fuel', value: 1.15 },
      { cost: 120, label: '+25% fuel', value: 1.25 },
      { cost: 200, label: '+40% fuel', value: 1.40 },
    ],
  },
  allBlastRadius: {
    key: 'allBlastRadius', category: 'shared', slot: 'launcher', name: 'Blast', description: 'Increase all weapon blast radius',
    icon: '💥', tiers: [
      { cost: 70,  label: '+10% blast', value: 1.10 },
      { cost: 140, label: '+20% blast', value: 1.20 },
      { cost: 240, label: '+30% blast', value: 1.30 },
    ],
  },
  armor: {
    key: 'armor', category: 'shared', slot: 'suit', name: 'Armor Plating', description: 'Flat damage reduction on all hits',
    icon: '🛡', tiers: [
      { cost: 100, label: '-3 damage taken', value: 3 },
      { cost: 200, label: '-6 damage taken', value: 6 },
      { cost: 350, label: '-10 damage taken', value: 10 },
    ],
  },

  // ---- Item upgrades (always visible) ----
  healBoost: {
    key: 'healBoost', category: 'item', slot: 'suit', name: 'Medkit', description: 'Unlock healthkits + boost effectiveness',
    icon: '💊', tiers: [
      { cost: 50,  label: 'Unlock + 25% healing', value: 1.25 },
      { cost: 100, label: '+50% healing', value: 1.50 },
      { cost: 175, label: '+100% healing', value: 2.00 },
    ],
  },
  probeRange: {
    key: 'probeRange', category: 'item', slot: 'launcher', name: 'Probe Range', description: 'Increase probe reveal area',
    icon: '📡', tiers: [
      { cost: 40,  label: '+20% reveal', value: 1.20 },
      { cost: 80,  label: '+40% reveal', value: 1.40 },
      { cost: 140, label: '+70% reveal', value: 1.70 },
    ],
  },
  preview: {
    key: 'preview', category: 'item', slot: 'launcher', name: 'Ballistic Computer', description: 'Longer, finer aim preview (value = preview quality 0–1)',
    icon: '🎯', tiers: [
      { cost: 60,  label: 'Medium preview', value: 0.6 },
      { cost: 120, label: 'Long preview', value: 0.8 },
      { cost: 200, label: 'Full flight preview', value: 1.0 },
    ],
  },
};
